import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Space, Divider } from 'antd';
import { LockOutlined, MailOutlined, GoogleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailLink,
  isSignInWithEmailLink,
  linkWithCredential,
  signOut,
  AuthCredential,
  User as FirebaseUser,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { useAuthStore, AuthUser } from '@shared/hooks/useAuthStore';
import { landingPathForRole } from '@shared/utils/roleAccess';
import { apiClient } from '@shared/api/client';
import { getFirebaseAuth, isFirebaseConfigured } from '@shared/firebase';
import { brand } from '@/theme/tokens';
import {
  LINK_ERROR,
  LinkError,
  isAccountExistsError,
  isPopupCancelled,
  linkErrorMessage,
  sameEmail,
} from './accountLinking';
import { LinkAccountPanel } from './LinkAccountPanel';

interface PendingLink {
  credential: AuthCredential;
  email: string;
}

const MAGIC_LINK_KEY = 'tc-magic-email';

export function LoginPage(): React.ReactElement {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const { setAuth } = useAuthStore();
  // T-222: credenziale Google rifiutata, in attesa del rientro col metodo di prima.
  const [pendingLink, setPendingLink] = useState<PendingLink | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  // Un doppio invio del form non deve collegare due volte.
  const linkInFlight = useRef(false);

  const devMock = import.meta.env.VITE_DEV_AUTH_MOCK === 'true';
  const firebaseReady = isFirebaseConfigured();

  const completeFirebaseLogin = async (firebaseUser: FirebaseUser): Promise<void> => {
    const idToken = await firebaseUser.getIdToken();
    useAuthStore.setState({ token: idToken });
    try {
      const res = await apiClient.get<AuthUser>('/users/me');
      setAuth(idToken, res.data);
      window.localStorage.removeItem(MAGIC_LINK_KEY);
      const redirect =
        (loc.state as { from?: { pathname: string } })?.from?.pathname ??
        landingPathForRole(res.data.role);
      nav(redirect, { replace: true });
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      useAuthStore.getState().logout();
      if (status === 401) {
        setErr('Account Firebase OK ma utente non registrato nel tenant. Contatta admin.');
      } else {
        setErr('Errore caricamento profilo');
      }
    }
  };

  // Handle magic link sign-in completion (when user lands on page from email)
  useEffect(() => {
    if (!firebaseReady) return;
    const auth = getFirebaseAuth();
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const stored = window.localStorage.getItem(MAGIC_LINK_KEY);
      const email = stored ?? window.prompt('Conferma email per completare il login') ?? '';
      if (!email) return;
      setLoading(true);
      signInWithEmailLink(auth, email, window.location.href)
        .then((cred) => completeFirebaseLogin(cred.user))
        .catch((e: Error) => setErr(e.message))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseReady]);

  const onEmailPwd = async (values: { email: string; password: string }): Promise<void> => {
    setErr(null);
    setInfo(null);
    setLoading(true);
    try {
      if (devMock || !firebaseReady) {
        const uid = values.email.split('@')[0];
        const token = `dev:${uid}:${values.email}`;
        useAuthStore.setState({ token });
        const res = await apiClient.get<AuthUser>('/users/me');
        setAuth(token, res.data);
        const redirect =
        (loc.state as { from?: { pathname: string } })?.from?.pathname ??
        landingPathForRole(res.data.role);
        nav(redirect, { replace: true });
      } else {
        const cred = await signInWithEmailAndPassword(getFirebaseAuth(), values.email, values.password);
        await completeFirebaseLogin(cred.user);
      }
    } catch (e) {
      const msg = (e as Error).message ?? t('login.error.invalid');
      setErr(msg.replace('Firebase: ', ''));
      useAuthStore.getState().logout();
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async (): Promise<void> => {
    setErr(null);
    setInfo(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(getFirebaseAuth(), provider);
      await completeFirebaseLogin(cred.user);
    } catch (e) {
      if (isPopupCancelled(e)) return;
      if (isAccountExistsError(e)) {
        // L'email ha gia' un account con un altro metodo: si rientra con
        // quello e poi si collega Google, invece di mostrare l'errore.
        const credential = GoogleAuthProvider.credentialFromError(e as FirebaseError);
        const email = (e as { customData?: { email?: string } }).customData?.email;
        if (credential && email) {
          setLinkErr(null);
          setPendingLink({ credential, email });
        } else {
          setErr(linkErrorMessage(new LinkError(LINK_ERROR.noPending)));
        }
        return;
      }
      setErr((e as Error).message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const linkWithPassword = async (password: string): Promise<void> => {
    if (linkInFlight.current) return;
    const pending = pendingLink;
    if (!pending) {
      setLinkErr(linkErrorMessage(new LinkError(LINK_ERROR.noPending)));
      return;
    }
    linkInFlight.current = true;
    setLinking(true);
    setLinkErr(null);
    try {
      const auth = getFirebaseAuth();
      const signed = await signInWithEmailAndPassword(auth, pending.email, password);
      if (!sameEmail(signed.user.email, pending.email)) {
        await signOut(auth).catch(() => undefined);
        throw new LinkError(LINK_ERROR.emailMismatch, signed.user.email);
      }
      await linkWithCredential(signed.user, pending.credential);
      setPendingLink(null);
      await completeFirebaseLogin(signed.user);
    } catch (e) {
      setLinkErr(linkErrorMessage(e, pending.email));
    } finally {
      linkInFlight.current = false;
      setLinking(false);
    }
  };

  const cancelLink = (): void => {
    setPendingLink(null);
    setLinkErr(null);
  };

  const onMagicLink = async (values: { magicEmail: string }): Promise<void> => {
    setErr(null);
    setInfo(null);
    setLoading(true);
    try {
      // Invio via backend (SES, template branded) invece di sendSignInLinkToEmail
      // del client SDK — l'email nativa Firebase non è branded né tracciata.
      // Il completamento resta invariato: isSignInWithEmailLink/signInWithEmailLink
      // sotto operano sullo stesso oobCode, generato lato server con lo stesso
      // continueUrl (Admin SDK generateSignInWithEmailLink).
      await apiClient.post('/auth/admin-magic-link', {
        email: values.magicEmail,
        continueUrl: window.location.origin + '/login',
      });
      window.localStorage.setItem(MAGIC_LINK_KEY, values.magicEmail);
      setInfo(`Se l'indirizzo è registrato riceverai a breve un'email con il link di accesso.`);
    } catch (e) {
      setErr((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Errore invio link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${brand.ink} 0%, ${brand.primaryDeep} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <Card style={{ width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <div
              style={{
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: 24,
                letterSpacing: '-0.5px',
                color: brand.ink,
                lineHeight: 1,
              }}
            >
              TERAMO CALCIO
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 2,
                color: brand.primary,
                marginTop: 4,
              }}
            >
              BACKOFFICE · 1913
            </div>
          </div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('login.title')}
          </Typography.Title>

          {devMock && (
            <Alert
              type="warning"
              showIcon
              message="Dev mock mode (VITE_DEV_AUTH_MOCK=true)"
              description="Email determina UID. Password ignorata."
            />
          )}
          {!firebaseReady && !devMock && (
            <Alert
              type="error"
              showIcon
              message="Firebase Web non configurato"
              description="Mancano VITE_FIREBASE_* env vars. Login impossibile."
            />
          )}

          {err && <Alert type="error" showIcon message={err} />}
          {info && <Alert type="success" showIcon message={info} />}

          {pendingLink ? (
            <LinkAccountPanel
              email={pendingLink.email}
              busy={linking}
              error={linkErr}
              onPassword={linkWithPassword}
              onCancel={cancelLink}
            />
          ) : !magicMode ? (
            <>
              {firebaseReady && (
                <Button
                  block
                  size="large"
                  icon={<GoogleOutlined />}
                  onClick={onGoogle}
                  loading={loading}
                >
                  Continua con Google
                </Button>
              )}

              <Divider style={{ margin: '12px 0' }}>oppure</Divider>

              <Form name="login" onFinish={onEmailPwd} layout="vertical">
                <Form.Item
                  name="email"
                  label={t('login.email')}
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input prefix={<MailOutlined />} placeholder="email@example.com" />
                </Form.Item>
                <Form.Item name="password" label={t('login.password')} rules={[{ required: true }]}>
                  <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
                </Form.Item>
                <Button type="primary" htmlType="submit" block loading={loading} size="large">
                  {t('login.submit')}
                </Button>
              </Form>

              {firebaseReady && (
                <Button type="link" block onClick={() => setMagicMode(true)}>
                  Login senza password (magic link)
                </Button>
              )}
            </>
          ) : (
            <>
              <Form name="magic" onFinish={onMagicLink} layout="vertical">
                <Form.Item
                  name="magicEmail"
                  label="Email per magic link"
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input prefix={<MailOutlined />} placeholder="email@example.com" />
                </Form.Item>
                <Button type="primary" htmlType="submit" block loading={loading} size="large">
                  Invia link
                </Button>
              </Form>
              <Button type="link" block onClick={() => setMagicMode(false)}>
                ← Torna a login normale
              </Button>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
